import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';

import { createDriveFolder } from '../../../services/entityDrive.js';

/**
 * Create a folder in the record's drive (CT-5717). One request per double click
 * (`submittingRef`); a toast either way; `onCreated` on success. On failure (a
 * taken name, a 422) the form stays with what the user typed.
 */
export function useCreateDriveFolder({ entityType, entityId, onCreated } = {}) {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const submittingRef = useRef(false);

  const create = useCallback(
    async ({ parentId, name, description, extractClaims }) => {
      if (submittingRef.current) return false;
      submittingRef.current = true;
      setCreating(true);
      try {
        await createDriveFolder({ entityType, entityId, parentId, name: name.trim(), description: description.trim(), extractClaims });
        toast.success({ title: t('knowledge.drive.newFolder.createdTitle'), message: t('knowledge.drive.newFolder.createdMessage', { name: name.trim() }) });
        onCreated?.();
        return true;
      } catch (err) {
        toast.error({ title: t('knowledge.drive.newFolder.errorTitle'), message: err?.message ?? t('knowledge.drive.newFolder.errorMessage') });
        return false;
      } finally {
        submittingRef.current = false;
        setCreating(false);
      }
    },
    [entityType, entityId, onCreated, toast, t],
  );

  return useMemo(() => ({ create, creating }), [create, creating]);
}

export default useCreateDriveFolder;
